import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ledgerApi } from '@/services/api';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatLedgerApiError, resolveLedgerOutflowPaymentAccount } from '../utils/ledgerEntreaMirror';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import {
  MemberMobileShell,
  MemberMobileHero,
  MemberMobileCollapse,
} from '../components/MemberMobileShell';
import LedgerFinanceKpiCards from '../components/LedgerFinanceKpiCards';
import UiSurface from '../components/UiSurface';
import ModalShell from '../components/ModalShell';
import MmSelect from '../components/MmSelect';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';
import { searchFieldsMatch } from '../utils/searchText';

const Financial = () => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const { hasPermission, role } = usePermissions();
  const { user } = useAuth();
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre = isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const canCreateFinancial = hasPermission('financial.create');
  const { error, success } = useToast();
  const { settings } = useSettings();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    type: 'contribution',
    date: new Date().toISOString().split('T')[0],
    status: 'completed',
    category: ''
  });

  const [ledgerBankDetails, setLedgerBankDetails] = useState([]);
  const [ledgerAccountsForDisplay, setLedgerAccountsForDisplay] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bdRes, accRes] = await Promise.all([
          ledgerApi.listBankDetails({ limit: 200, skip: 0 }),
          ledgerApi.listAccounts({ include_inactive: false }),
        ]);
        if (cancelled) return;
        const bdRaw = bdRes.data;
        setLedgerBankDetails(Array.isArray(bdRaw?.bank_details) ? bdRaw.bank_details : []);
        const accRaw = accRes.data;
        setLedgerAccountsForDisplay(Array.isArray(accRaw?.accounts) ? accRaw.accounts : []);
      } catch (e) {
        console.error('Ledger bank details (Financial page):', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const ledgerAccountLabel = (id) => {
    if (!id) return '';
    const a = ledgerAccountsForDisplay.find((x) => String(x.id) === String(id));
    return a?.name || '';
  };

  useEffect(() => {
    loadData();
  }, [transactionsPage, itemsPerPage]);

  useEffect(() => {
    filterTransactions();
  }, [transactions, searchQuery, selectedType, selectedStatus]);

  const loadData = async () => {
    try {
      const params = {
        limit: itemsPerPage,
        skip: (transactionsPage - 1) * itemsPerPage
      };
      const [transactionsRes, statsRes] = await Promise.all([
        ledgerApi.listTransactions(params),
        ledgerApi.getStatsSummary(),
      ]);

      const raw = Array.isArray(transactionsRes.data)
        ? transactionsRes.data
        : (transactionsRes.data.transactions || []);
      const normalizedLedger = raw.map((t) => {
        const isCredit = t.tx_type === 'credit';
        const isPending = t.status === 'pending_validation';
        const meta = t.metadata && typeof t.metadata === 'object' ? t.metadata : {};
        const cat = String(t.category || '').toLowerCase();
        const sourceType = String(meta.source_type || '').toLowerCase();
        const expenseKind = String(meta.expense_kind || '').toLowerCase();
        const isAllocationOut =
          !isCredit &&
          (cat === 'aide' ||
            (cat === 'depense' && expenseKind === 'allocation') ||
            sourceType === 'assistance_case' ||
            Boolean(meta.synthesized_from_assistance_case));
        return {
          ...t,
          type: isCredit ? 'contribution' : 'expense',
          isAllocationOut,
          /** Clé i18n (financial.*) : les allocations payées sont des débits (sorties de caisse). */
          displayTypeKey: isCredit
            ? 'movementContribution'
            : isAllocationOut
              ? 'movementAllocation'
              : 'movementExpense',
          date: String(t.occurred_at || t.created_at || '').slice(0, 10),
          uiStatus: isPending ? 'pending' : (t.status === 'rejected' ? 'cancelled' : 'completed'),
          requires_approval: isPending,
          is_approved: !isPending,
          amount: Number(t.amount || 0),
        };
      });
      const normalized = [...normalizedLedger].sort((a, b) => {
        const da = String(a?.occurred_at || a?.date || '');
        const db = String(b?.occurred_at || b?.date || '');
        return db.localeCompare(da);
      });
      setTransactions(normalized);
      setFilteredTransactions(normalized);
      const listCountFromStats = Number(statsRes.data?.volume?.count ?? 0);
      setTransactionsTotal(
        listCountFromStats > 0 ? listCountFromStats : normalizedLedger.length
      );

      const byType = (statsRes.data?.by_tx_type || []).reduce((acc, row) => {
        const k = String(row.tx_type ?? row._id ?? '').toLowerCase();
        if (!k) return acc;
        acc[k] = Number(row.total || 0);
        return acc;
      }, {});
      /** KPI globaux : agrégats Ledger uniquement (toutes écritures), jamais la seule page chargée. */
      const totalIncome = Number(byType.credit || 0);
      const totalExpenses = Number(byType.debit || 0);
      setStats({
        total_balance: totalIncome - totalExpenses,
        total_income: totalIncome,
        total_expenses: totalExpenses,
      });
    } catch (err) {
      console.error('Failed to load financial data:', err);
      error(t('financial.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const amount = parseFloat(formData.amount);
      if (!(amount > 0)) {
        error(t('financial.amountMustBePositive'));
        return;
      }

      const { payment_method_id, account_id } = await resolveLedgerOutflowPaymentAccount(ledgerApi);
      if (!payment_method_id && !account_id) {
        error(t('financial.ledgerNoAccount'));
        return;
      }

      const body = {
        tx_type: formData.type === 'contribution' ? 'credit' : 'debit',
        category: formData.category || (formData.type === 'contribution' ? 'cotisation' : 'depense'),
        amount,
        description: formData.description,
        occurred_at: formData.date,
        pending_validation: formData.status === 'pending',
        ...(payment_method_id ? { payment_method_id } : {}),
        ...(account_id ? { account_id } : {}),
      };
      await ledgerApi.createTransaction(body);
      success(t('financial.createSuccess'));
      
      loadData();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error(formatLedgerApiError(err), err);
      error(formatLedgerApiError(err, t('financial.saveError')));
    }
  };

  const handleApprove = async (id) => {
    try {
      await ledgerApi.approveTransaction(id);
      success(t('financial.approveSuccess'));
      loadData();
    } catch (err) {
      console.error(formatLedgerApiError(err), err);
      error(formatLedgerApiError(err, t('financial.approveError')));
    }
  };

  const filterTransactions = () => {
    let filtered = transactions;

    // Filtre par type
    if (selectedType !== 'all') {
      filtered = filtered.filter(t => t.type === selectedType);
    }

    // Filtre par statut
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(t => t.uiStatus === selectedStatus);
    }

    // Recherche par description ou catégorie
    if (searchQuery.trim()) {
      filtered = filtered.filter((t) =>
        searchFieldsMatch(searchQuery, [t.description, t.category])
      );
    }

    setFilteredTransactions(filtered);
  };

  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      type: 'contribution',
      date: new Date().toISOString().split('T')[0],
      status: 'completed',
      category: ''
    });
  };

  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const totalBalance = toNumber(stats?.total_balance);
  const totalIncome = toNumber(stats?.total_contributions ?? stats?.total_income);
  const totalExpenses = toNumber(stats?.total_expenses);

  const transactionsTotalPages = Math.max(1, Math.ceil(transactionsTotal / itemsPerPage));
  const renderTransactionsPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={transactionsPage}
      totalPages={transactionsTotalPages}
      totalItems={transactionsTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(page) => {
        setTransactionsPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={(n) => {
        setItemsPerPage(n);
        setTransactionsPage(1);
      }}
    />
  );

  if (loading) {
    return (
      <div className="space-y-3 sm:space-y-4 animate-pulse">
        <div className="md:hidden rounded-2xl bg-white ring-1 ring-slate-200/80 shadow-sm overflow-hidden animate-pulse">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 space-y-2">
            <div className="h-2.5 w-20 rounded bg-slate-200" />
            <div className="h-5 w-48 rounded bg-slate-200" />
          </div>
          <div className="px-4 py-3 space-y-2">
            <div className="h-3 w-full max-w-xs rounded bg-slate-100" />
            <div className="h-8 w-40 rounded-lg bg-slate-100" />
          </div>
        </div>
        <div className={`grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3 ${isMembre ? 'hidden md:grid' : ''}`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-4 space-y-3">
              <div className="h-10 w-10 rounded-xl bg-slate-200" />
              <div className="h-4 w-1/2 rounded bg-slate-200" />
              <div className="h-6 w-2/3 rounded bg-slate-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] space-y-3 sm:space-y-4">
      {/* Header */}
      <div className={isMembre ? 'hidden md:block' : ''}>
      <PageHeader
        title={t('financial.pageTitle')}
        subtitle={t('financial.pageSubtitle')}
        actions={
          <div className="flex items-center gap-2">
            {canCreateFinancial && (
              <button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="px-4 py-2 bg-[#113a5b] text-white rounded-lg hover:bg-[#0f3350] transition font-medium text-sm whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f6fd4]"
              >
                {t('financial.newTransaction')}
              </button>
            )}
          </div>
        }
      />
      </div>

      {isMembre && (
        <div className="hidden md:block rounded-xl border border-[#bcd4f6] bg-[#edf4ff]/60 px-3 py-2.5 text-sm text-[#0f3350] md:px-4 md:py-3 md:mx-0">
          <Link
            to="/my-fund"
            className="font-semibold text-[#113a5b] underline-offset-2 hover:underline"
          >
            {t('financial.memberOrgLink')}
          </Link>
          <span className="text-slate-600 md:inline">
            <span className="hidden md:inline"> — </span>
            <span className="mt-1 block text-xs leading-snug text-slate-600 md:mt-0 md:inline md:text-sm">
              {t('financial.memberOrgBlurb')}
            </span>
          </span>
        </div>
      )}

      {isMembre ? (
        <MemberMobileShell>
          <MemberMobileHero
            label={t('financial.introKicker')}
            title={`${totalBalance.toLocaleString(localeFmt)} FBU`}
            hint={t('financial.kpiBalanceShort')}
            tone="info"
            actions={
              <>
                <Link
                  to="/my-fund"
                  className="mm-cta mm-cta--ghost flex-1"
                >
                  {t('financial.memberOrgLink')}
                </Link>
                {canCreateFinancial ? (
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowForm(true);
                    }}
                    className="mm-cta flex-1"
                  >
                    {t('financial.newTransaction')}
                  </button>
                ) : null}
              </>
            }
          >
            <div className="mt-3 flex gap-3 text-xs">
              <span className="font-semibold text-emerald-800">
                + {totalIncome.toLocaleString(localeFmt)}
              </span>
              <span className="font-semibold text-red-800">
                − {totalExpenses.toLocaleString(localeFmt)}
              </span>
            </div>
          </MemberMobileHero>
          <MemberMobileCollapse title={t('financial.filtersTypeStatus')} defaultOpen={false}>
            <div className="flex flex-wrap gap-1.5 py-1">
              {[
                { value: 'all', label: t('financial.filterAll') },
                { value: 'contribution', label: t('financial.filterInflow') },
                { value: 'expense', label: t('financial.filterExpense') },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedType(opt.value)}
                  className={`mm-chip ${
                    selectedType === opt.value ? 'mm-chip--on' : 'mm-chip--off'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </MemberMobileCollapse>
        </MemberMobileShell>
      ) : null}

      {/* KPI : web par défaut */}
      <UiSurface id="financial.kpi_grid">
        <LedgerFinanceKpiCards
          soldeActuel={totalBalance}
          totalEntrees={totalIncome}
          totalSorties={totalExpenses}
          loading={loading}
        />
      </UiSurface>

      {/* Barre de recherche et filtres — desktop / admin ; membre mobile utilise le collapse */}
      <div className={isMembre ? 'hidden md:block' : ''}>
      <SearchAndFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchInputClassName="min-h-[44px] md:min-h-0"
        collapseSearchOnMobile
        mobileFiltersToggleLabel={t('financial.filtersTypeStatus')}
        className="max-md:!space-y-2 max-md:!p-2.5"
        placeholder={isMembre ? t('financial.searchPlaceholderMember') : t('financial.searchPlaceholderAdmin')}
        filters={[
          {
            key: 'type',
            label: t('financial.filterType'),
            type: 'button-group',
            value: selectedType,
            onChange: setSelectedType,
            options: [
              { value: 'all', label: t('financial.filterAll') },
              { value: 'contribution', label: t('financial.filterInflow') },
              { value: 'expense', label: t('financial.filterExpense') },
              { value: 'other', label: t('financial.filterOther') }
            ]
          },
          {
            key: 'status',
            label: t('financial.filterStatus'),
            type: 'button-group',
            value: selectedStatus,
            onChange: setSelectedStatus,
            options: [
              { value: 'all', label: t('financial.filterAll') },
              { value: 'completed', label: t('financial.filterPosted') },
              { value: 'pending', label: t('financial.filterPending') },
              { value: 'cancelled', label: t('financial.filterRejected') }
            ]
          }
        ]}
      />
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {renderTransactionsPagination('top')}
        <div className="space-y-3 px-1 pt-1">
        {filteredTransactions.map((transaction) => {
          const isIn = transaction.type === 'contribution';
          const borderAccent = isIn ? 'border-l-emerald-500' : 'border-l-red-500';
          const typePill = isIn
            ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
            : 'bg-red-50 text-red-900 ring-1 ring-red-200';
          const statusPill =
            transaction.uiStatus === 'completed'
              ? 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
              : transaction.uiStatus === 'pending'
                ? 'bg-amber-50 text-amber-950 ring-1 ring-amber-200'
                : 'bg-red-50 text-red-900 ring-1 ring-red-200';

          return (
          <div
            key={transaction.id}
            className={`mm-card p-3.5 border-l-4 ${borderAccent}`}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[11px] font-medium tabular-nums text-slate-500">{transaction.date}</span>
              <span className={`max-w-[55%] truncate rounded-full px-2.5 py-0.5 text-[10px] font-bold ${typePill}`}>
                {isIn ? t('financial.flowEntry') : transaction.isAllocationOut ? t('financial.flowAllocation') : t('financial.flowOut')}
              </span>
            </div>
            <p className="line-clamp-2 text-sm font-semibold leading-snug text-[#0f3350]">{transaction.description}</p>
            <div className="mt-2 flex items-end justify-between gap-2">
              <p className="text-lg font-bold tabular-nums tracking-tight text-[#0f3350]">
                {isIn ? '+' : '-'}
                {transaction.amount.toLocaleString(localeFmt)}
                <span className="ml-1 align-top text-[10px] font-semibold text-slate-500">FBU</span>
              </p>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${statusPill}`}>
                {transaction.uiStatus === 'completed'
                  ? t('financial.statusShortOk')
                  : transaction.uiStatus === 'pending'
                    ? t('financial.statusShortWait')
                    : t('financial.statusShortRejected')}
              </span>
            </div>
          </div>
          );
        })}
        {filteredTransactions.length === 0 && transactions.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-sm">{t('financial.emptyFiltered')}</p>
          </div>
        )}
        {transactions.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-sm">{t('financial.emptyAll')}</p>
          </div>
        )}
        </div>
        {renderTransactionsPagination('bottom')}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        {renderTransactionsPagination('top')}
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('financial.colDate')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('financial.colDescription')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('financial.colType')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('financial.colAmount')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('financial.colStatus')}
              </th>
              {!isMembre && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('financial.colActions')}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTransactions.map((transaction) => (
              <tr key={transaction.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.date}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{transaction.description}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    transaction.type === 'contribution' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {t(`financial.${transaction.displayTypeKey}`)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {transaction.amount.toLocaleString(localeFmt)} FBU
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    transaction.uiStatus === 'completed' ? 'bg-green-100 text-green-800' :
                    transaction.uiStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {transaction.uiStatus === 'completed' ? t('financial.statusPosted') :
                     transaction.uiStatus === 'pending' ? t('financial.statusPending') : t('financial.statusRejected')}
                  </span>
                </td>
                {!isMembre && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {transaction.requires_approval && !transaction.is_approved && hasPermission('financial.approve') && (
                      <button
                        onClick={() => handleApprove(transaction.id)}
                        className="text-green-600 hover:text-green-900 mr-4 font-medium"
                        title={t('financial.approveTitle')}
                      >
                        {t('financial.approve')}
                      </button>
                    )}
                    <span className="text-xs text-gray-400">{t('financial.ledgeraBadge')}</span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {renderTransactionsPagination('bottom')}
      </div>

      {/* Bank Info (Ledgera) + signataires projet */}
      {settings && (ledgerBankDetails.length > 0 || settings.signatories?.length > 0) && (
        <div className="rounded-2xl border border-gray-200 bg-white p-3 sm:rounded-lg sm:p-6">
          <h2 className="mb-3 text-base font-semibold text-gray-900 sm:mb-4 sm:text-lg">{t('financial.bankInfoTitle')}</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            {ledgerBankDetails.length > 0 && (
              <div>
                <h3 className="text-base font-medium text-gray-900 mb-3">{t('financial.bankCoordinates')}</h3>
                <div className="space-y-4">
                  {ledgerBankDetails.map((bd) => (
                    <div key={bd.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <span className="text-sm font-semibold text-gray-900">{bd.bank_name}</span>
                        {bd.ledger_account_id && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                            {ledgerAccountLabel(bd.ledger_account_id)}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        {bd.metadata?.account_holder_label && (
                          <div>
                            <span className="text-gray-500">{t('financial.bankHolder')}</span> {bd.metadata.account_holder_label}
                          </div>
                        )}
                        {bd.account_number && (
                          <div>
                            <span className="text-gray-500">{t('financial.bankAccountNumber')}</span> {bd.account_number}
                          </div>
                        )}
                        {bd.iban && (
                          <div>
                            <span className="text-gray-500">{t('financial.bankIban')}</span> {bd.iban}
                          </div>
                        )}
                        {bd.bic_swift && (
                          <div>
                            <span className="text-gray-500">{t('financial.bankBic')}</span> {bd.bic_swift}
                          </div>
                        )}
                        {bd.branch_name && (
                          <div>
                            <span className="text-gray-500">{t('financial.bankBranch')}</span> {bd.branch_name}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Signatories */}
            {settings.signatories && settings.signatories.length > 0 && (
              <div>
                <h3 className="text-base font-medium text-gray-900 mb-3">{t('financial.signatoriesTitle')}</h3>
                <ul className="space-y-2">
                  {settings.signatories.filter(s => s.is_active).map((signatory, index) => (
                    <li key={signatory.id || index} className="text-sm text-gray-600 border border-gray-200 rounded-lg p-3">
                      <span className="font-medium text-gray-900">{signatory.name}</span>
                      {signatory.role && <span className="text-gray-500"> – {signatory.role}</span>}
                      {signatory.position && <span className="text-gray-500"> ({signatory.position})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <ModalShell
          maxWidthClass="max-w-2xl"
          panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
          onBackdropClick={() => {
            setShowForm(false);
            resetForm();
          }}
        >
          <div className="mm-modal-header">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]">{t('financial.pageTitle', 'Finances')}</p>
              <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                {t('financial.modalNewTitle')}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              aria-label={t('financial.cancel')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="space-y-3 overflow-y-auto px-4 py-3 sm:space-y-4 sm:px-5 sm:py-4">
              <div className="mm-card space-y-3 p-3 sm:p-3.5">
                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('financial.labelDescription')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mm-field"
                    required
                    rows={3}
                    placeholder={t('financial.placeholderDescription')}
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      {t('financial.labelAmountFbu')}
                    </label>
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="mm-field"
                      required
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      {t('financial.labelEntryType')}
                    </label>
                    <MmSelect
                      value={formData.type}
                      onChange={(type) => setFormData({ ...formData, type })}
                      required
                      placeholder={t('financial.labelEntryType')}
                      options={[
                        { value: 'contribution', label: t('financial.optionCredit') },
                        { value: 'expense', label: t('financial.optionExpense') },
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className="mm-card space-y-3 p-3 sm:p-3.5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      {t('financial.labelDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="mm-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                      {t('financial.labelStatus')}
                    </label>
                    <MmSelect
                      value={formData.status}
                      onChange={(status) => setFormData({ ...formData, status })}
                      required
                      placeholder={t('financial.labelStatus')}
                      options={[
                        { value: 'completed', label: t('financial.optionPostNow') },
                        { value: 'pending', label: t('financial.optionPending') },
                      ]}
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {t('financial.labelCategoryOptional')}
                  </label>
                  <input
                    type="text"
                    value={formData.category || ''}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="mm-field"
                    placeholder={t('financial.placeholderCategory')}
                  />
                </div>
              </div>
            </div>

            <div className="mm-modal-footer">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="mm-cta mm-cta--ghost flex-1"
              >
                {t('financial.cancel')}
              </button>
              <button type="submit" className="mm-cta flex-1">
                {t('financial.create')}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {isMembre && canCreateFinancial && (
        <div className="md:hidden fixed right-4 z-40 bottom-[calc(env(safe-area-inset-bottom)+8.75rem)]">
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            aria-label={t('financial.fabAddAria')}
            className="mm-cta h-12 rounded-2xl px-4 text-sm shadow-lg"
          >
            {t('financial.fabAddTransaction')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Financial;
